import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { useMediaSrc } from '../../library/useMediaSrc'
import { aiTag } from '../aiLayer'
import { formatChatTime, type ChatRow } from '../chatRows'
import { Icon } from '../Icon'
import { BuyingPowerDots, SpentBadge } from '../RowBadges'
import { Sparkles } from './phoneIcons'
import './phone.css'

/** The two buttons behind a row, 78 px each (design). */
export const REVEAL_PX = 156
/** Let go past this and the buttons stay open. */
const REVEAL_OPEN = 60

interface Props {
  row: ChatRow
  nameLoading: boolean
  ai: { status?: unknown; vibe?: unknown } | undefined
  show: { spent: boolean; buyingPower: boolean; shadeCreators: boolean }
  buyingPower: { value: unknown; known: boolean }
  now: Date
  /** This row's buttons are showing (one row at a time). */
  revealed: boolean
  onReveal: (open: boolean) => void
  onOpen: () => void
  onToggleRead: () => void
  onTogglePin: () => void
}

type Gesture = { id: number; x: number; y: number; axis: 'x' | 'y' | null; base: number; dx: number }

/**
 * A chat row on the phone (handoff-19): avatar with the online dot, name,
 * badges and time over the last message, the AI's mark and the unread count.
 * Swiped left it uncovers Read/Unread and Pin.
 */
export function PhoneChatRow({ row, nameLoading, ai, show, buyingPower, now, revealed, onReveal, onOpen, onToggleRead, onTogglePin }: Props) {
  const { t, i18n } = useTranslation('chats')
  const avatar = useMediaSrc(row.avatar ?? '')
  const gesture = useRef<Gesture | null>(null)
  const swiped = useRef(false)
  const [drag, setDrag] = useState<number | null>(null)
  const x = drag ?? (revealed ? -REVEAL_PX : 0)
  const tag = ai ? aiTag(ai.status, ai.vibe) : null
  const initials = (row.named ? row.name : '')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
  const unread = row.unread > 0
  const act = (fn: () => void) => () => {
    fn()
    onReveal(false)
  }

  return (
    <div role="listitem" className="ph-row-li">
      <div className="ph-row-actions" aria-hidden={!revealed}>
        <button type="button" className="ph-row-act is-read" tabIndex={revealed ? 0 : -1} onClick={act(onToggleRead)}>
          <FeatherIcon name={unread ? 'eye' : 'eye-off'} size={20} />
          {unread ? t('swipe.read') : t('swipe.unread')}
        </button>
        <button type="button" className="ph-row-act is-pin" tabIndex={revealed ? 0 : -1} onClick={act(onTogglePin)}>
          <Icon id="icon-pin" size={20} />
          {row.pinned ? t('phone.unpin') : t('phone.pin')}
        </button>
      </div>
      <button
        type="button"
        className={['ph-row', drag != null && 'is-dragging', unread && 'has-unread', row.muted && 'is-muted', show.shadeCreators && row.performer && 'is-performer'].filter(Boolean).join(' ')}
        data-chat-fan-id={row.fanId}
        style={x ? { transform: `translateX(${x}px)` } : undefined}
        onPointerDown={(e) => {
          if (e.button !== 0) return
          swiped.current = false
          gesture.current = { id: e.pointerId, x: e.clientX, y: e.clientY, axis: null, base: revealed ? -REVEAL_PX : 0, dx: revealed ? -REVEAL_PX : 0 }
        }}
        onPointerMove={(e) => {
          const g = gesture.current
          if (!g || g.id !== e.pointerId) return
          const dx = e.clientX - g.x
          const dy = e.clientY - g.y
          if (!g.axis) {
            if (Math.max(Math.abs(dx), Math.abs(dy)) < 7) return
            g.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
            if (g.axis === 'x') e.currentTarget.setPointerCapture?.(e.pointerId)
          }
          if (g.axis !== 'x') return
          swiped.current = true
          g.dx = Math.max(-REVEAL_PX, Math.min(0, g.base + dx))
          setDrag(g.dx)
        }}
        onPointerUp={(e) => {
          const g = gesture.current
          gesture.current = null
          if (!g || g.id !== e.pointerId || g.axis !== 'x') return
          setDrag(null)
          onReveal(g.dx < -REVEAL_OPEN)
        }}
        onPointerCancel={() => {
          gesture.current = null
          setDrag(null)
        }}
        onDragStart={(e) => e.preventDefault()}
        onClick={() => {
          if (swiped.current) return
          if (revealed) onReveal(false)
          else onOpen()
        }}
      >
        <span className="ph-row-avatar">
          <span className="ph-row-pic" ref={avatar.ref as React.RefObject<HTMLSpanElement>}>
            {avatar.src ? <img src={avatar.src} alt="" loading="lazy" /> : initials}
          </span>
          {row.online && <span className="ph-row-online" role="img" aria-label={t('phone.online')} />}
        </span>
        <span className="ph-row-main">
          <span className="ph-row-top">
            {nameLoading ? (
              <span className="ph-row-name is-loading" role="img" aria-label={t('loadingName')} />
            ) : (
              <span className="ph-row-name">{row.name}</span>
            )}
            {show.spent && !nameLoading && row.spent != null && <SpentBadge amount={row.spent} />}
            {show.buyingPower && <BuyingPowerDots value={buyingPower.value} known={buyingPower.known} />}
            {row.pinned && (
              <span className="ph-row-mark is-pin" role="img" aria-label={t('pinned')}>
                <Icon id="icon-pin" size={12} />
              </span>
            )}
            {row.muted && (
              <span className="ph-row-mark" role="img" aria-label={t('muted')}>
                <FeatherIcon name="bell-off" size={13} />
              </span>
            )}
            <span className="ph-row-time">{formatChatTime(row.time, now, i18n.language, t('yesterday'))}</span>
          </span>
          <span className="ph-row-bottom">
            <span className="ph-row-preview">
              {row.fromMe && row.preview.kind !== 'empty' && <span className="ph-row-you">{t('phone.you')} </span>}
              {row.preview.kind === 'text' ? row.preview.text : row.preview.kind === 'media' ? t('phone.media') : '—'}
            </span>
            {tag === 'needs_you' ? (
              <span className="ph-row-needs">{t('tabs.needs_you')}</span>
            ) : tag && tag !== 'ignores' ? (
              <span className="ph-row-ai" role="img" aria-label={t('phone.aiReplying')}>
                <Sparkles size={15} />
              </span>
            ) : null}
            {unread && (
              <span className="ph-row-unread" role="img" aria-label={t('unread')}>
                {row.unread > 99 ? '99+' : row.unread}
              </span>
            )}
          </span>
        </span>
      </button>
    </div>
  )
}
